/**
 * Chat persistence utilities for localStorage synchronization
 */

import { CACHE_CONFIG } from '../config/reactQuery';
import type {
  CachedMessage,
  ChatCache,
  QuestionContext,
  ChatRoomParticipant,
  CacheOperationResult,
  OfflineMessage,
} from '@models/view';
import { logger } from './logger';

/**
 * Safe JSON parsing with error handling
 */
const safeJsonParse = <T>(jsonString: string | null, fallback: T): T => {
  if (!jsonString) return fallback;

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    logger.error('Failed to parse JSON from localStorage:', error);
    return fallback;
  }
};

/**
 * Safe localStorage operations with quota management
 */
const safeLsSet = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (
      error instanceof DOMException &&
      (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    ) {
      logger.warn('localStorage quota exceeded, clearing old cache');
      clearOldestCacheEntries();
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (retryError) {
        logger.error('Failed to save after cache cleanup:', retryError);
        return false;
      }
    }
    logger.error('Failed to save to localStorage:', error);
    return false;
  }
};

/**
 * Clear oldest cache entries when quota is exceeded
 */
const clearOldestCacheEntries = (): void => {
  const keysToCheck = Object.keys(localStorage).filter(
    key =>
      key.startsWith(CACHE_CONFIG.STORAGE_KEYS.MESSAGES) ||
      key.startsWith(CACHE_CONFIG.STORAGE_KEYS.ROOMS),
  );

  // Sort by timestamp if available, otherwise by key
  keysToCheck.sort((a, b) => {
    const timeA = localStorage.getItem(`${a}_timestamp`) || '0';
    const timeB = localStorage.getItem(`${b}_timestamp`) || '0';
    return timeA.localeCompare(timeB);
  });

  // Remove oldest 25% of entries
  const toRemove = Math.ceil(keysToCheck.length * 0.25);
  keysToCheck.slice(0, toRemove).forEach(key => {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_timestamp`);
  });
};

/**
 * Cache versioning for data migrations
 */
const CACHE_VERSION = 2;

/**
 * Enhanced message cache operations
 */
const getMessageCacheKey = (roomId: string): string => {
  return `${CACHE_CONFIG.STORAGE_KEYS.MESSAGES}${roomId}`;
};

const getMessageTimestampKey = (roomId: string): string => {
  return `${CACHE_CONFIG.STORAGE_KEYS.LAST_FETCH}${roomId}`;
};

export const MessageCache = {
  /**
   * Save messages to cache with enhanced metadata
   */
  save(
    roomId: string,
    messages: CachedMessage[],
    questionContext?: QuestionContext,
    participants?: ChatRoomParticipant[],
  ): CacheOperationResult {
    const startTime = performance.now();

    try {
      // Validate input
      if (!roomId || !Array.isArray(messages)) {
        throw new Error('Invalid input: roomId and messages array required');
      }

      // Limit message count for performance
      const limitedMessages = messages.slice(-CACHE_CONFIG.MAX_MESSAGES_PER_ROOM);

      // Enhance messages with cache metadata
      const enhancedMessages: CachedMessage[] = limitedMessages.map(msg => ({
        ...msg,
        cached_at: msg.cached_at,
        last_updated: new Date().toISOString(),
        network_state_when_created: msg.network_state_when_created,
        status: msg.status,
      }));

      // Create cache structure
      const cache: ChatCache = {
        room_id: roomId,
        messages: enhancedMessages,
        participants: participants || [],
        question_context: questionContext,
        metadata: {
          last_fetched: new Date().toISOString(),
          last_message_timestamp: enhancedMessages[enhancedMessages.length - 1]?.timestamp,
          unread_count: 0,
          cache_version: CACHE_VERSION,
          total_messages: enhancedMessages.length,
        },
        performance_stats: {
          cache_hits: 0,
          cache_misses: 0,
        },
      };

      // Save to localStorage
      const cacheKey = getMessageCacheKey(roomId);
      const timestampKey = getMessageTimestampKey(roomId);

      const success =
        safeLsSet(cacheKey, JSON.stringify(cache)) &&
        safeLsSet(timestampKey, cache.metadata.last_fetched);

      const executionTime = performance.now() - startTime;

      return {
        success,
        operation: 'write',
        affected_items: enhancedMessages.length,
        execution_time_ms: executionTime,
        cache_state: 'fresh',
        error_message: success ? undefined : 'Failed to save to localStorage',
      };
    } catch (error) {
      const executionTime = performance.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error(`[MessageCache] Failed to save messages for room ${roomId}:`, error);

      return {
        success: false,
        operation: 'write',
        affected_items: 0,
        execution_time_ms: executionTime,
        error_message: errorMessage,
      };
    }
  },

  /**
   * Load messages from cache with integrity checking
   */
  load(roomId: string): {
    cache: ChatCache | null;
    result: CacheOperationResult;
  } {
    const startTime = performance.now();

    try {
      if (!roomId) {
        throw new Error('Room ID is required');
      }

      const cacheKey = getMessageCacheKey(roomId);
      const cachedData = localStorage.getItem(cacheKey);

      if (!cachedData) {
        const executionTime = performance.now() - startTime;
        return {
          cache: null,
          result: {
            success: false,
            operation: 'read',
            affected_items: 0,
            execution_time_ms: executionTime,
            error_message: 'No cached data found',
            cache_state: 'expired',
          },
        };
      }

      const parsed = safeJsonParse<unknown>(cachedData, null);
      if (!parsed || typeof parsed !== 'object') {
        const executionTime = performance.now() - startTime;
        return {
          cache: null,
          result: {
            success: false,
            operation: 'read',
            affected_items: 0,
            execution_time_ms: executionTime,
            error_message: 'Invalid cached data',
            cache_state: 'expired',
          },
        };
      }

      const maybeCache = parsed as Partial<ChatCache>;

      if (maybeCache.metadata?.cache_version !== CACHE_VERSION) {
        // Handle cache version mismatch
        MessageCache.clear(roomId);
        const executionTime = performance.now() - startTime;
        return {
          cache: null,
          result: {
            success: false,
            operation: 'read',
            affected_items: 0,
            execution_time_ms: executionTime,
            error_message: 'Cache version mismatch, cleared stale data',
            cache_state: 'expired',
          },
        };
      }

      // Validate cache integrity
      if (!Array.isArray(maybeCache.messages)) {
        throw new Error('Invalid cache structure: messages array missing');
      }

      const cache = maybeCache as ChatCache;

      // Determine cache staleness
      const lastFetch = new Date(cache.metadata.last_fetched);
      const now = new Date();
      const ageMs = now.getTime() - lastFetch.getTime();
      const isStale = ageMs > 5 * 60 * 1000; // 5 minutes

      const executionTime = performance.now() - startTime;

      if (cache.performance_stats) {
        cache.performance_stats.cache_hits += 1;
      }

      return {
        cache,
        result: {
          success: true,
          operation: 'read',
          affected_items: cache.messages.length,
          execution_time_ms: executionTime,
          cache_state: isStale ? 'stale' : 'fresh',
          sync_required: isStale,
        },
      };
    } catch (error) {
      const executionTime = performance.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error(`[MessageCache] Failed to load messages for room ${roomId}:`, error);

      return {
        cache: null,
        result: {
          success: false,
          operation: 'read',
          affected_items: 0,
          execution_time_ms: executionTime,
          error_message: errorMessage,
        },
      };
    }
  },

  /**
   * Get messages only, discarding cache metadata
   */
  getMessages(roomId: string): CachedMessage[] {
    const { cache } = MessageCache.load(roomId);
    return cache?.messages ?? [];
  },

  /**
   * Get last fetch timestamp
   */
  getLastFetchTime(roomId: string): string | null {
    const timestampKey = getMessageTimestampKey(roomId);
    return localStorage.getItem(timestampKey);
  },

  /**
   * Merge new messages with existing cache
   */
  merge(
    roomId: string,
    newMessages: CachedMessage[],
    questionContext?: QuestionContext,
  ): CacheOperationResult {
    const { cache: existingCache } = MessageCache.load(roomId);

    if (!existingCache) {
      return MessageCache.save(roomId, newMessages, questionContext);
    }

    // Merge messages, removing duplicates by ID
    const messageMap = new Map<string, CachedMessage>();

    // Add existing messages
    existingCache.messages.forEach(msg => messageMap.set(msg.id, msg));

    // Add/update with new messages
    newMessages.forEach(msg =>
      messageMap.set(msg.id, {
        ...msg,
        cached_at: msg.cached_at,
        last_updated: new Date().toISOString(),
        network_state_when_created: msg.network_state_when_created,
      }),
    );

    // Convert back to array and sort by timestamp
    const mergedMessages = Array.from(messageMap.values()).sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    return MessageCache.save(
      roomId,
      mergedMessages,
      questionContext || existingCache.question_context,
      existingCache.participants,
    );
  },

  /**
   * Clear cache for specific room
   */
  clear(roomId: string): CacheOperationResult {
    const startTime = performance.now();

    try {
      const cacheKey = getMessageCacheKey(roomId);
      const timestampKey = getMessageTimestampKey(roomId);

      localStorage.removeItem(cacheKey);
      localStorage.removeItem(timestampKey);

      const executionTime = performance.now() - startTime;

      return {
        success: true,
        operation: 'delete',
        affected_items: 1,
        execution_time_ms: executionTime,
      };
    } catch (error) {
      const executionTime = performance.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      return {
        success: false,
        operation: 'delete',
        affected_items: 0,
        execution_time_ms: executionTime,
        error_message: errorMessage,
      };
    }
  },

  /**
   * Clear all message caches
   */
  clearAll(): CacheOperationResult {
    const startTime = performance.now();
    let clearedCount = 0;

    try {
      const keysToRemove = Object.keys(localStorage).filter(
        key =>
          key.startsWith(CACHE_CONFIG.STORAGE_KEYS.MESSAGES) ||
          key.startsWith(CACHE_CONFIG.STORAGE_KEYS.LAST_FETCH),
      );

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        clearedCount++;
      });

      const executionTime = performance.now() - startTime;

      return {
        success: true,
        operation: 'delete',
        affected_items: clearedCount,
        execution_time_ms: executionTime,
      };
    } catch (error) {
      const executionTime = performance.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      return {
        success: false,
        operation: 'delete',
        affected_items: clearedCount,
        execution_time_ms: executionTime,
        error_message: errorMessage,
      };
    }
  },

  /**
   * Get cache statistics for monitoring
   */
  getStats(): {
    total_rooms: number;
    total_messages: number;
    total_size_bytes: number;
    oldest_cache: string | null;
    newest_cache: string | null;
  } {
    const messageKeys = Object.keys(localStorage).filter(key =>
      key.startsWith(CACHE_CONFIG.STORAGE_KEYS.MESSAGES),
    );

    let totalMessages = 0;
    let totalSize = 0;
    let oldestCache: string | null = null;
    let newestCache: string | null = null;

    messageKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        totalSize += data.length;
        try {
          const cache = JSON.parse(data) as ChatCache;
          totalMessages += cache.messages.length;

          const fetchTime = cache.metadata.last_fetched;
          if (!oldestCache || fetchTime < oldestCache) {
            oldestCache = fetchTime;
          }
          if (!newestCache || fetchTime > newestCache) {
            newestCache = fetchTime;
          }
        } catch (error) {
          logger.warn('Failed to parse cache for stats:', key, error);
        }
      }
    });

    return {
      total_rooms: messageKeys.length,
      total_messages: totalMessages,
      total_size_bytes: totalSize,
      oldest_cache: oldestCache,
      newest_cache: newestCache,
    };
  },
} as const;

/**
 * Offline message queue management
 */
const getOfflineQueueKey = (): string => {
  return CACHE_CONFIG.STORAGE_KEYS.QUEUE;
};

export const OfflineMessageQueue = {
  /**
   * Add message to offline queue
   */
  enqueue(message: OfflineMessage): boolean {
    try {
      const queue = OfflineMessageQueue.getQueue();

      // Prevent duplicate messages
      if (queue.some(m => m.id === message.id)) {
        return true; // Already queued
      }

      // Limit queue size
      if (queue.length >= CACHE_CONFIG.MAX_OFFLINE_QUEUE) {
        queue.shift(); // Remove oldest
      }

      queue.push(message);
      return safeLsSet(getOfflineQueueKey(), JSON.stringify(queue));
    } catch (error) {
      logger.error('Failed to enqueue offline message:', error);
      return false;
    }
  },

  /**
   * Remove message from queue
   */
  dequeue(messageId: string): boolean {
    try {
      const queue = OfflineMessageQueue.getQueue();
      const filtered = queue.filter(m => m.id !== messageId);

      return safeLsSet(getOfflineQueueKey(), JSON.stringify(filtered));
    } catch (error) {
      logger.error('Failed to dequeue offline message:', error);
      return false;
    }
  },

  /**
   * Get all queued messages
   */
  getQueue(): OfflineMessage[] {
    const queueData = localStorage.getItem(getOfflineQueueKey());
    return safeJsonParse(queueData, []);
  },

  /**
   * Clear all queued messages
   */
  clear(): boolean {
    try {
      localStorage.removeItem(getOfflineQueueKey());
      return true;
    } catch (error) {
      logger.error('Failed to clear offline queue:', error);
      return false;
    }
  },

  /**
   * Get queue size
   */
  size(): number {
    return OfflineMessageQueue.getQueue().length;
  },
} as const;

export { CACHE_VERSION };
export const chatPersistence = {
  MessageCache,
  OfflineMessageQueue,
};
